<template>
  <FormLayout
    :has-changes="hasChanges"
    :is-saving="isOverwritingTags"
    title="Tags"
    @on-dismiss="handleDismiss"
    @on-save="handleSave"
  >
    <TagsEditor
      v-if="isNewJob ? true : isTagsFetched"
      ref="tagsEditor"
      :initial-tags="initialTags"
      :hub-id="currentHub?.id"
      @error="handleError"
    />
    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ snackbarMessage ?? '' }}
      </vs-snackbar>
    </template>
  </FormLayout>
</template>

<script setup lang="ts">
import FormLayout from '@/components/common/FormLayout.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import TagsEditor from '@/components/common/tags/TagsEditor.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import { computed, ref, toRef } from 'vue'
import { BulkUpdateTagsOperation, useBulkUpdateTags, useGetJobTagsQuery } from '@/api/jobs'
import { useHubs } from '@/common/useHubs'
import { useJobFormDataStore } from '@/components/jobs/jobFormDataStore'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import NewTag from '@/models/new_tag'

const props = defineProps<{
  jobId?: string | null // Job UUID - If null, then we are creating new jobs
}>()
const isNewJob = computed(() => !props.jobId)
const jobFormDataStore = useJobFormDataStore()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const { currentHub } = useHubs()

const isSnackbarVisible = ref(false)
const snackbarMessage = ref()
const snackbarType = ref(SnackType.ERROR)

const emit = defineEmits(['tags-updated'])
const jobIdRef = toRef(props, 'jobId')
const { data: tags, isFetched: isTagsFetched } = useGetJobTagsQuery(jobIdRef)
const { mutateAsync: overwriteTags, isLoading: isOverwritingTags } = useBulkUpdateTags()

const initialTags = computed(() => {
  if (props.jobId) {
    return tags.value || []
  }
  return jobFormDataStore.draftTags
})

const tagsEditor = ref()
const hasChanges = computed(() => tagsEditor.value?.hasChanges ?? false)
const handleSave = async () => {
  if (!jobIdRef.value) {
    jobFormDataStore.setDraftTags(
      tagsEditor.value?.currentTags.filter((tag) => tag.isEditable) ?? [],
    )
  } else {
    try {
      await overwriteTags({
        jobIds: [jobIdRef.value],
        tags:
          tagsEditor.value?.currentTags
            .filter((tag: NewTag) => tag.isEditable)
            .map((tag: NewTag) => NewTag.toObject(tag).toDisplayString) ?? [],
        operation: BulkUpdateTagsOperation.OVERWRITE,
      })
    } catch {
      handleError('Failed to save tags. Please try again.')
      return
    }
  }

  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isTagBottomSheetVisible: false,
  })
  emit('tags-updated')
}

const handleDismiss = () => {
  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      return
    }
  }
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isTagBottomSheetVisible: false,
  })
}

const handleError = (err: any) => {
  snackbarMessage.value = err
  isSnackbarVisible.value = true
}
</script>
